import React from 'react';
import { ChevronDown, ChevronRight, History, SquarePen } from 'lucide-react';
import { InventoryItemDetails } from '../InventoryItemDetails';
import { formatCurrency } from '../../../utils/format';
import type { InventoryItem } from '../../../types/inventory';
import type { CuppingProfile } from '../../../services/profileService';
import type {
  RoastProfileContext,
  FinishedGoodsTasteProfileContext,
} from '../../../hooks/inventory-hooks/useInventoryPageState';
import { useLanguage } from '../../../features/language/useLanguage';

interface InventoryMobileListProps {
  items: InventoryItem[];
  expandedRowIds: Set<string>;
  onToggleRow: (id: string) => void;
  currencySymbol: string;
  onRowClick: (item: InventoryItem) => void;
  onEditItem: (item: InventoryItem) => void;
  onOpenHistory: (item: InventoryItem) => void;
  onOpenRoastProfile: (context: RoastProfileContext) => void;
  onOpenTasteProfile: (context: FinishedGoodsTasteProfileContext) => void;
  onRestock: (item: InventoryItem) => void;
  canWrite: boolean;
  formatDate: (dateString: string | Date) => string;
}

export const InventoryMobileList: React.FC<InventoryMobileListProps> = ({
  items,
  expandedRowIds,
  onToggleRow,
  currencySymbol,
  onRowClick,
  onEditItem,
  onOpenHistory,
  onOpenRoastProfile,
  onOpenTasteProfile,
  onRestock,
  canWrite,
  formatDate,
}) => {
  const { t } = useLanguage();
  const getStockUnitLabel = (item: InventoryItem) => (
    item.itemType === 'finished_good' ? t('inventory.basicInfo.units.pcs') : item.unitOfMeasure
  );
  const getDerivedStockStatus = (item: InventoryItem): 'available' | 'low' | 'depleted' => {
    const stock = typeof item.stock === 'number' ? item.stock : 0;
    if (stock <= 0) return 'depleted';
    if (item.itemType === 'finished_good') {
      if (stock <= 10) return 'low';
      return 'available';
    }
    const reorderPoint = typeof item.reorderPoint === 'number' ? item.reorderPoint : 0;
    if (reorderPoint > 0 && stock <= reorderPoint) return 'low';
    return 'available';
  };

  const renderStatusBadge = (item: InventoryItem) => {
    const derived = getDerivedStockStatus(item);
    let statusLabel = t('inventory.table.status.available');
    let statusClass = 'bg-green-100 text-green-800';
    if (derived === 'depleted') {
      statusLabel = t('inventory.table.status.depleted');
      statusClass = 'bg-red-100 text-red-800';
    } else if (derived === 'low') {
      statusLabel = t('inventory.table.status.low');
      statusClass = 'bg-yellow-100 text-yellow-800';
    }
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
        {statusLabel}
      </span>
    );
  };

  return (
    <div className="md:hidden divide-y divide-gray-200">
      {items.map((item) => {
        const isExpanded = expandedRowIds.has(item.id);
        const derivedStockStatus = getDerivedStockStatus(item);
        const finishedGoodsTasteProfileExists =
          item.itemType === 'finished_good' &&
          item.cuppingProfiles?.some((p: CuppingProfile) =>
            p.type === 'ItemType' &&
            (p.cupping_type === 'FinishedGoodsTaste' || p.extended_data?.profile_variant === 'finished_goods_simple')
          );
        return (
          <div
            key={item.id}
            className="p-4 space-y-3 bg-white cursor-pointer"
            onClick={() => onRowClick(item)}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-start gap-2">
                {item.itemType !== 'roasted_bulk' && (
                  <button
                    data-test-id={`inventory-mobile-expand-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleRow(item.id);
                    }}
                    className="mt-0.5 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                  <p className="text-sm text-gray-500">{item.code}</p>
                </div>
              </div>
              {renderStatusBadge(item)}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm text-gray-500">
              <div>
                <span className="block text-xs text-gray-400">{t('inventory.table.columns.type')}</span>
                <span className="capitalize">{item.itemType.replace('_', ' ')}</span>
              </div>
              <div>
                <span className="block text-xs text-gray-400">{t('forms.common.status')}</span>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                    item.status === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {item.status}
                </span>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenHistory(item);
                }}
                className="cursor-pointer"
              >
                <span className="block text-xs text-gray-400">{t('inventory.table.columns.stock')}</span>
                <span
                  className={`${derivedStockStatus === 'available' ? '' : 'text-red-600 font-medium'} hover:underline`}
                >
                  {(typeof item.stock === 'number' ? item.stock : 0)} {getStockUnitLabel(item)}
                </span>
              </div>
              <div>
                <span className="block text-xs text-gray-400">{t('inventory.table.columns.unitCost')}</span>
                {item.standardCost ? formatCurrency(item.standardCost, currencySymbol) : t('settingsMcp.values.none')}
              </div>
              <div>
                <span className="block text-xs text-gray-400">{t('inventory.table.columns.estimatedPrice')}</span>
                {item.itemType === 'roasted_bulk' || item.itemType === 'green_bean' ? (
                  <span className="text-gray-400">{t('inventory.modal.values.na')}</span>
                ) : typeof item.estimatedSellingPrice === 'number' ? (
                  formatCurrency(item.estimatedSellingPrice, currencySymbol)
                ) : (
                  t('settingsMcp.values.none')
                )}
              </div>
              <div>
                <span className="block text-xs text-gray-400">{t('inventory.lotDetail.fields.expiryDate')}</span>
                {item.expiryDate ? formatDate(new Date(item.expiryDate)) : t('settingsMcp.values.none')}
              </div>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
              <div className="flex flex-wrap gap-2">
                {item.itemType !== 'finished_good' && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenRoastProfile({
                          itemId: item.id,
                          itemName: item.name,
                          workOrderId: item.woReferenceId,
                        });
                      }}
                      data-test-id={`inventory-mobile-roast-profile-${item.id}`}
                      className="text-xs px-2 py-1 rounded-md bg-gray-200 text-gray-700"
                    >
                      {t('inventory.mobile.actions.roast')}
                    </button>
                    {canWrite && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditItem(item);
                        }}
                        data-test-id={`inventory-mobile-bean-profile-${item.id}`}
                        className="text-xs px-2 py-1 rounded-md bg-gray-200 text-gray-700"
                      >
                        {t('inventory.mobile.actions.bean')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenTasteProfile({ item, mode: 'view' });
                      }}
                      data-test-id={`inventory-mobile-taste-profile-${item.id}`}
                      className="text-xs px-2 py-1 rounded-md bg-gray-200 text-gray-700"
                    >
                      {t('inventory.mobile.actions.taste')}
                    </button>
                  </>
                )}
                {item.itemType === 'finished_good' && (
                  <>
                    {canWrite && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenTasteProfile({ item, mode: 'add' });
                        }}
                        data-test-id={`inventory-mobile-fg-taste-profile-add-${item.id}`}
                        className="text-xs px-2 py-1 rounded-md bg-gray-200 text-gray-700"
                      >
                        {t('inventory.table.actions.add')}
                      </button>
                    )}
                    {finishedGoodsTasteProfileExists && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenTasteProfile({ item, mode: 'view' });
                        }}
                        data-test-id={`inventory-mobile-fg-taste-profile-view-${item.id}`}
                        className="text-xs px-2 py-1 rounded-md bg-primary-500 text-white"
                      >
                        {t('reports.shift.view')}
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {canWrite && (
                  <button
                    data-test-id={`inventory-edit-item-button-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditItem(item);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-700 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors"
                  >
                    <SquarePen className="w-4 h-4" />
                    {t('purchasing.supplierList.actions.edit')}
                  </button>
                )}
                <button
                  data-test-id={`inventory-history-item-button-${item.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenHistory(item);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                >
                  <History className="w-4 h-4" />
                  {t('inventory.roastedBulk.history')}
                </button>
                {canWrite && derivedStockStatus !== 'available' && (
                  <button
                    data-test-id={`inventory-restock-item-button-${item.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRestock(item);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                  >
                    {t('inventory.table.actions.restock')}
                  </button>
                )}
              </div>
            </div>

            {isExpanded && item.itemType !== 'roasted_bulk' && (
              <div className="mt-2 pt-2 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                <InventoryItemDetails
                  key={`${item.id}-${item.stock}-${item.status}`}
                  item={item}
                  canWrite={canWrite}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
